import { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Sparkles, X, ArrowRight, Loader2, Send, Bot } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { growthAdvisorRecommend } from "@/lib/api";

// Site-wide floating Growth Advisor. Renders on every page except the
// /growth-advisor full page (avoids the duplicate) and the raw preview iframes.
const HIDDEN_PREFIXES = ["/growth-advisor", "/launch/preview", "/assistant/", "/crescent"];

const QUESTIONS = [
  { key: "business_type", q: "Hi! I'm the WebsiteTech Growth Advisor. What kind of business do you run?", placeholder: "e.g. Dental clinic, restaurant, HVAC" },
  { key: "location", q: "Great. Where are your customers based?", placeholder: "City, region or country" },
  { key: "offerings", q: "What are your main services or products?", placeholder: "e.g. Family dentistry, invisalign" },
  { key: "has_website", q: "Do you currently have a website?", options: [
    { v: "yes", l: "Yes, and it's modern" },
    { v: "outdated", l: "Yes, but it's outdated" },
    { v: "no", l: "Not yet" },
  ] },
  { key: "current_leads", q: "How many leads do you receive today?", options: [
    { v: "0–10 / month", l: "0–10 per month" },
    { v: "10–50 / month", l: "10–50 per month" },
    { v: "50–200 / month", l: "50–200 per month" },
    { v: "200+ / month", l: "200+ per month" },
  ] },
  { key: "primary_goal", q: "Last one — what's your #1 goal right now?", options: [
    { v: "More website visitors", l: "More visitors" },
    { v: "More phone calls", l: "More phone calls" },
    { v: "More inquiries", l: "More inquiries" },
    { v: "More appointments", l: "More appointments" },
    { v: "More online sales", l: "More online sales" },
    { v: "More local customers", l: "More local customers" },
    { v: "More B2B customers", l: "More B2B customers" },
  ] },
];

export default function GrowthAdvisorWidget() {
  const location = useLocation();
  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0); // 0..QUESTIONS.length-1  → then results
  const [answers, setAnswers] = useState({});
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  const scrollRef = useRef(null);

  // Scroll to bottom whenever we advance
  useEffect(() => {
    if (!open) return;
    scrollRef.current?.scrollTo?.({ top: 99999, behavior: "smooth" });
  }, [step, plan, open]);

  const answer = async (key, value) => {
    if (!value) return;
    const next = { ...answers, [key]: value };
    setAnswers(next);
    setText("");
    if (step < QUESTIONS.length - 1) {
      setStep(step + 1);
      return;
    }
    // All questions answered → recommend
    setBusy(true);
    try {
      const r = await growthAdvisorRecommend(next);
      setPlan(r);
    } catch (e) {
      toast.error("The advisor is busy — please try again in a moment.");
    } finally { setBusy(false); }
  };

  const reset = () => {
    setStep(0); setAnswers({}); setPlan(null); setText("");
  };

  if (hidden) return null;

  const currentQ = QUESTIONS[step];

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 group flex items-center gap-2 pl-4 pr-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white shadow-xl transition-all hover:scale-105 hover:shadow-2xl"
          data-testid="growth-advisor-open"
          aria-label="Open Growth Advisor"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
          </span>
          <Sparkles size={16} />
          <span className="text-sm font-semibold">Growth Advisor</span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-md rounded-2xl bg-white shadow-2xl border border-slate-200 flex flex-col overflow-hidden" style={{ maxHeight: "min(85vh, 640px)" }} data-testid="growth-advisor-panel">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-900 text-white">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
                <Bot size={16} />
              </div>
              <div>
                <div className="font-bold text-sm">WebsiteTech Growth Advisor</div>
                <div className="text-[10px] text-white/60 uppercase tracking-widest">AI · Free · 60 seconds</div>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white" aria-label="Close" data-testid="growth-advisor-close">
              <X size={18} />
            </button>
          </div>

          {/* Body */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-slate-50">
            {/* Q&A history bubbles */}
            {QUESTIONS.slice(0, step).map((q, i) => (
              <div key={q.key} className="space-y-2">
                <div className="text-sm text-slate-800 bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]">{q.q}</div>
                {answers[q.key] && (
                  <div className="text-sm text-white bg-slate-900 rounded-2xl rounded-br-sm px-3 py-2 max-w-[85%] ml-auto" data-testid={`growth-advisor-answer-${q.key}`}>{answers[q.key]}</div>
                )}
              </div>
            ))}

            {/* Current question or plan */}
            {!plan && currentQ && !busy && (
              <div className="space-y-3">
                <div className="text-sm text-slate-800 bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%]" data-testid={`growth-advisor-q-${currentQ.key}`}>{currentQ.q}</div>
                {currentQ.options ? (
                  <div className="grid grid-cols-2 gap-2">
                    {currentQ.options.map((o) => (
                      <button
                        key={o.v}
                        onClick={() => answer(currentQ.key, o.v)}
                        className="text-xs font-semibold text-slate-800 bg-white border border-slate-300 hover:border-slate-500 hover:bg-slate-100 rounded-full px-3 py-1.5 transition-colors"
                        data-testid={`growth-advisor-option-${currentQ.key}-${o.v}`}
                      >
                        {o.l}
                      </button>
                    ))}
                  </div>
                ) : (
                  <form onSubmit={(e) => { e.preventDefault(); answer(currentQ.key, text.trim()); }} className="flex gap-2 pt-1">
                    <Input
                      autoFocus
                      value={text}
                      onChange={(e) => setText(e.target.value)}
                      placeholder={currentQ.placeholder}
                      className="flex-1 h-10 text-sm"
                      data-testid={`growth-advisor-input-${currentQ.key}`}
                    />
                    <Button type="submit" size="sm" className="h-10 bg-slate-900 hover:bg-slate-800 rounded-full px-3" data-testid={`growth-advisor-send-${currentQ.key}`}>
                      <Send size={14} />
                    </Button>
                  </form>
                )}
              </div>
            )}

            {busy && (
              <div className="text-sm text-slate-500 bg-white border border-slate-200 rounded-2xl rounded-tl-sm px-3 py-2 max-w-[85%] inline-flex items-center gap-2">
                <Loader2 size={14} className="animate-spin" /> Thinking about your growth plan…
              </div>
            )}

            {plan && (
              <div className="space-y-4" data-testid="growth-advisor-plan">
                <div className="bg-white border border-emerald-200 rounded-2xl p-4">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Recommended Growth Plan</div>
                  <div className="font-bold text-slate-900 text-base">{plan.headline || "Your personalized plan"}</div>
                  {plan.summary && <div className="text-sm text-slate-600 mt-2">{plan.summary}</div>}
                </div>

                {plan.suggested_bundle && (() => {
                  const bundleName = (plan.suggested_bundle.name || plan.suggested_bundle || "").toString();
                  const alias = bundleName.toLowerCase().replace(/websitetech\s*/i, "").trim().split(/\s+/)[0];
                  const highlight = alias ? `?highlight=${alias}` : "";
                  return (
                    <div className="bg-white border border-emerald-200 rounded-2xl p-4">
                      <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Best-fit package</div>
                      <div className="font-bold text-slate-900" data-testid="growth-advisor-package-name">{bundleName}</div>
                      {plan.suggested_bundle.why && <div className="text-xs text-slate-600 mt-1">{plan.suggested_bundle.why}</div>}
                      <div className="mt-3 flex flex-wrap gap-3 items-center">
                        <Link to={`/lead-generation/bundles${highlight}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:text-emerald-900" data-testid="growth-advisor-package-link">
                          See bundle details <ArrowRight size={12} />
                        </Link>
                        <Link to="/contact?topic=lead-generation-bundle" className="inline-flex items-center gap-1 text-xs font-semibold text-slate-900 hover:text-brand" data-testid="growth-advisor-proposal-link">
                          Request a proposal →
                        </Link>
                      </div>
                    </div>
                  );
                })()}

                {(plan.recommended || []).length > 0 && (
                  <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold px-1">Top services for you</div>
                    {plan.recommended.slice(0, 4).map((s, i) => (
                      <Link
                        key={s.slug || s.name || i}
                        to={s.url || (s.slug ? `/lead-generation/${s.slug}` : "/lead-generation")}
                        className="flex items-start gap-2 bg-white border border-slate-200 hover:border-slate-400 rounded-xl p-3 transition-colors"
                        data-testid={`growth-advisor-service-${i}`}
                      >
                        <div className="font-bold text-slate-500 text-sm">{i + 1}.</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-slate-900 text-sm">{s.name || s.title || s.label}</div>
                          {s.why && <div className="text-xs text-slate-600 mt-0.5 line-clamp-2">{s.why}</div>}
                        </div>
                        <ArrowRight size={14} className="text-slate-400 mt-1" />
                      </Link>
                    ))}
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm" className="bg-slate-900 hover:bg-slate-800 rounded-full flex-1">
                    <Link to="/growth-advisor" data-testid="growth-advisor-full-page">Open full advisor →</Link>
                  </Button>
                  <Button size="sm" variant="outline" onClick={reset} className="rounded-full" data-testid="growth-advisor-restart">
                    Start over
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer step indicator */}
          {!plan && (
            <div className="px-4 py-2 border-t border-slate-100 bg-white text-[10px] text-slate-400 uppercase tracking-widest text-center">
              Step {Math.min(step + 1, QUESTIONS.length)} of {QUESTIONS.length}
            </div>
          )}
        </div>
      )}
    </>
  );
}
